import React, { useRef, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Paper, Button, Typography } from "@material-ui/core";
import { useFormik } from "formik";
import * as dayjs from "dayjs";
import useStyles from "@/styles/alarmStyle";
import SearchInput from "@/components/SearchInput";
import { ScheduleTemplateListDatatable } from "@/helpers/scheduleTemplateListDatatable";
import { useTranslation } from "@/langs/useTranslation";
import { toast } from "@/apis/request";
import { useGlobalState } from "@/GlobalProvider";
import HasPermission from "@/components/commons/HasPermission";
import ScheduleTemplateCreators from "@/components/FormFields/ScheduleTemplateCreators";
import { FormItem } from "@/components/Form";
import ScheduleTasks from "@/components/FormFields/ScheduleTasks";
import TemplateStatus from "@/components/FormFields/TemplateStatus";
import TagList from "@/components/FormFields/TagList";
import DateRangePicker from "@/components/DateRangePicker";
import { scheduleTemplatesActionApi } from "@/apis/apiCollection";
import modalConfirm from "@/components/Modal/modalConfirm";
import SubmitButton from "@/components/commons/SubmitButton";
import { IconFile } from "@/components/Icon";
import DeviceGroups from "@/components/FormFields/DeviceGroups/DeviceGroups";
import ViewDevicesModal from "./ViewDevicesModal";
import CollapsePanel, {
  CollapseItem,
  CollapseTrigger
} from "@/components/CollapsePanel";

const TemplateList = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const tableRef = useRef(null);
  const selectedRowsRef = useRef([]);
  const isReset = useRef(true);
  const operateType = useRef(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const viewDeviceId = useRef(null);
  const [viewDeviceModalOpen, setViewDeviceModalOpen] = useState(false);

  const initialValues = {
    taskType: undefined,
    status: undefined,
    group: undefined,
    tag: undefined,
    creator: undefined,
    date: [null, null],
    keywords: ""
  };

  const history = useHistory();
  const { currentCompanyId } = useGlobalState();

  const { values, handleChange, handleSubmit, handleReset } = useFormik({
    initialValues,
    onSubmit: () => {
      handleReloadDataTable();
    }
  });

  const getSearchParams = () => {
    const formValues = isReset.current ? initialValues : values;
    const date = formValues.date;
    return {
      task_type: formValues.taskType,
      status: formValues.status,
      group_ids: formValues.group,
      tag_ids: formValues.tag,
      create_user_id: formValues.creator,
      searching: formValues.keywords,
      start_time: date[0] ? dayjs.utc(date[0]).toISOString() : undefined,
      end_time: date[1] ? dayjs.utc(date[1]).toISOString() : undefined
    };
  };

  const reloadTableWithGroupId = () => {
    isReset.current = false;
    tableRef.current?.reload();
  };

  const resetTableWithGroupId = () => {
    isReset.current = true;
    tableRef.current?.reload();
  };

  const handleReloadDataTable = reset => {
    if (reset) {
      resetTableWithGroupId();
    } else {
      reloadTableWithGroupId();
    }
  };

  const resetQuery = () => {
    handleReset();
    handleReloadDataTable(true);
  };

  const getSelectedRows = () => {
    const selectedIds = tableRef.current.getSelected().map(id => id.split(","));

    const data = tableRef.current?.api().data().toArray();
    const selectedRows = selectedIds.map(id =>
      data.find(
        item =>
          item.schedule_template_id === id[0] && item.schedule_id === id[1]
      )
    );

    if (selectedRows && selectedRows.length) {
      selectedRowsRef.current = selectedRows;
      return selectedRows;
    }
    toast.info(t({ defaultMessage: "未选择任何模板" }));
  };

  const handleLoaded = json => {
    setTotal(json.recordsTotal);
  };

  const handleViewDevices = row => {
    viewDeviceId.current = row.schedule_id;
    setViewDeviceModalOpen(true);
  };

  const doEnable = async (ids, enable) => {
    try {
      setLoading(true);
      operateType.current = enable ? "enable" : "disable";
      if (!Array.isArray(ids)) {
        ids = [ids];
      }
      await scheduleTemplatesActionApi({
        status: enable ? 1 : 2,
        action: "status",
        schedule_template_ids: ids
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "操作成功" }));
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = row => {
    modalConfirm({
      title: t({ defaultMessage: "禁用排程模板" }),
      content: t(
        {
          defaultMessage:
            "是否确认禁用（{name}）该排程模板？禁用后设备无法引用该模板。"
        },
        { name: row.schedule_template_name }
      ),
      onConfirm: () => {
        return doEnable(row.schedule_template_id, false);
      }
    });
  };

  const handleEnable = row => {
    modalConfirm({
      title: t({ defaultMessage: "启用排程模板" }),
      content: t(
        {
          defaultMessage:
            "是否确认启用（{name}）该排程模板？启用后设备可以引用该模板。"
        },
        { name: row.schedule_template_name }
      ),
      onConfirm: () => {
        return doEnable(row.schedule_template_id, true);
      }
    });
  };

  const doDelete = async ids => {
    try {
      setLoading(true);
      operateType.current = "delete";
      if (!Array.isArray(ids)) {
        ids = [ids];
      }
      await scheduleTemplatesActionApi({
        action: "delete",
        schedule_template_ids: ids
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "删除成功" }));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = row => {
    modalConfirm({
      title: t({ defaultMessage: "删除排程模板" }),
      content: t(
        {
          defaultMessage: "是否确认删除（{name}）该排程模板？"
        },
        { name: row.schedule_template_name }
      ),
      onConfirm: () => {
        return doDelete(row.schedule_template_id);
      }
    });
  };

  const handleBatchDisable = () => {
    if (getSelectedRows()) {
      modalConfirm({
        title: t({ defaultMessage: "禁用排程模板" }),
        content: t(
          {
            defaultMessage:
              "是否确认禁用（{name}）该排程模板？禁用后设备无法引用该模板。"
          },
          {
            name: [
              ...new Set(
                selectedRowsRef.current.map(item => item.schedule_template_name)
              )
            ].join("、")
          }
        ),
        onConfirm: () => {
          return doEnable(
            [
              ...new Set(
                selectedRowsRef.current.map(item => item.schedule_template_id)
              )
            ],
            false
          );
        }
      });
    }
  };

  const handleBatchEnable = () => {
    if (getSelectedRows()) {
      modalConfirm({
        title: t({ defaultMessage: "启用排程模板" }),
        content: t(
          {
            defaultMessage:
              "是否确认启用（{name}）该排程模板？启用后设备可以引用该模板。"
          },
          {
            name: [
              ...new Set(
                selectedRowsRef.current.map(item => item.schedule_template_name)
              )
            ].join("、")
          }
        ),
        onConfirm: () => {
          return doEnable(
            [
              ...new Set(
                selectedRowsRef.current.map(item => item.schedule_template_id)
              )
            ],
            true
          );
        }
      });
    }
  };

  const handleBatchDelete = () => {
    if (getSelectedRows()) {
      modalConfirm({
        title: t({ defaultMessage: "删除排程模板" }),
        content: t(
          {
            defaultMessage: "是否确认删除（{name}）该排程模板？"
          },
          {
            name: [
              ...new Set(
                selectedRowsRef.current.map(item => item.schedule_template_name)
              )
            ].join("、")
          }
        ),
        onConfirm: () => {
          return doDelete([
            ...new Set(
              selectedRowsRef.current.map(item => item.schedule_template_id)
            )
          ]);
        }
      });
    }
  };

  useEffect(() => {
    resetQuery();
  }, [currentCompanyId]);

  return (
    <div className={classes.main}>
      <Paper elevation={0}>
        <form onSubmit={handleSubmit}>
          <div className={classes.tableHeader}>
            <div className={classes.tableTop}>
              <div>
                <Typography variant="h5" component="span">
                  {t({ defaultMessage: "排程模板列表" })}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  component="span"
                  className={classes.subTitle}
                >
                  <IconFile size={15} />
                  {t({ defaultMessage: "{num}条" }, { num: total })}
                </Typography>
              </div>
              <div className={classes.rightAction}>
                <SearchInput
                  placeholder={t({ defaultMessage: "搜索" })}
                  name="keywords"
                  icon={null}
                  value={values.keywords}
                  onChange={handleChange}
                />
                <HasPermission permission="schedule_task_policy_create">
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => {
                      history.push("/schedule/template/create");
                    }}
                  >
                    {t({ defaultMessage: "添加排程模板" })}
                  </Button>
                </HasPermission>
              </div>
            </div>
            <CollapsePanel>
              <div className={classes.tableQueryForm}>
                <FormItem
                  label={t({ defaultMessage: "排程任务" })}
                  className={classes.formItem}
                >
                  <ScheduleTasks
                    name="taskType"
                    value={values.taskType}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "模板状态" })}
                  className={classes.formItem}
                >
                  <TemplateStatus
                    name="status"
                    value={values.status}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "门店" })}
                  className={classes.formItem}
                >
                  <DeviceGroups
                    name="group"
                    value={values.group}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "标签" })}
                  className={classes.formItem}
                >
                  <TagList
                    name="tag"
                    value={values.tag}
                    onChange={handleChange}
                  />
                </FormItem>
                <CollapseItem>
                  <FormItem
                    label={t({ defaultMessage: "创建人" })}
                    className={classes.formItem}
                  >
                    <ScheduleTemplateCreators
                      name="creator"
                      value={values.creator}
                      onChange={handleChange}
                    />
                  </FormItem>
                  <FormItem
                    label={t({ defaultMessage: "创建时间" })}
                    className={classes.formItem}
                  >
                    <DateRangePicker
                      name="date"
                      value={values.date}
                      onChange={handleChange}
                    />
                  </FormItem>
                </CollapseItem>
                <FormItem className={classes.formItem}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{ marginRight: 8 }}
                  >
                    {t({ defaultMessage: "查询" })}
                  </Button>
                  <Button variant="outlined" onClick={resetQuery}>
                    {t({ defaultMessage: "重置" })}
                  </Button>
                  <CollapseTrigger />
                </FormItem>
              </div>
            </CollapsePanel>
          </div>
        </form>
        <ScheduleTemplateListDatatable
          ref={tableRef}
          getSearchParams={getSearchParams}
          onView={data => {
            history.push(
              `/schedule/template/info/${data.schedule_template_id}`
            );
          }}
          onEdit={data => {
            history.push(
              `/schedule/template/edit/${data.schedule_template_id}`
            );
          }}
          onViewDevices={handleViewDevices}
          onDisable={handleDisable}
          onEnable={handleEnable}
          onDelete={handleDelete}
          onLoaded={handleLoaded}
          footer={
            <>
              <HasPermission permission="schedule_task_policy_enable">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  loading={loading && operateType.current === "disable"}
                  onClick={handleBatchDisable}
                >
                  {t({ defaultMessage: "批量禁用" })}
                </SubmitButton>
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  loading={loading && operateType.current === "enable"}
                  onClick={handleBatchEnable}
                >
                  {t({ defaultMessage: "批量启用" })}
                </SubmitButton>
              </HasPermission>
              <HasPermission permission="schedule_task_policy_delete">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  loading={loading && operateType.current === "delete"}
                  onClick={handleBatchDelete}
                >
                  {t({ defaultMessage: "批量删除" })}
                </SubmitButton>
              </HasPermission>
            </>
          }
        />
      </Paper>
      <ViewDevicesModal
        open={viewDeviceModalOpen}
        scheduleId={viewDeviceId.current}
        onClose={() => setViewDeviceModalOpen(false)}
      />
    </div>
  );
};

export default TemplateList;
